import {
  useGetAllWishlistItemsByUserQuery,
  useGetAllWishlistsQuery,
  useIsTravelApp,
  wishlistsApi,
} from "@capone/common";
import { Box } from "@material-ui/core";
import {
  HotelShopRoomTypePickerEnum,
  HotelShopRoomTypePickerVariant,
  TrackingEventControlType,
  useDeviceTypes,
  useTrackingEventControl,
} from "halifax";
import queryStringParser from "query-string";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { RouteComponentProps } from "react-router-dom";

import { STAYS_WISHLIST } from "@capone/experiments";
import { useSelector } from "react-redux";
import {
  CallState,
  CfarQuoteResultEnum,
  HotelBookType,
  QuoteId,
  RoomInfoProductsWithTransformedIndexes,
  RoomProductType,
  RoomProductVariant,
  RoomProductWithTransformedIndexes,
  SELECT_HOTEL_ROOM,
  VIEWED_HOTEL_DETAILS,
  getNthNightTrackingProperty,
  getSelectHotelRoomHotelCfarOfferFacts,
  getViewedHotelDetailsHotelCfarOfferFacts,
} from "redmond";
import { ClientContext } from "../../App";
import { trackEvent } from "../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  CONTROL,
  DESKTOP_RECENTLY_VIEWED_HOTELS,
  DESKTOP_RECENTLY_VIEWED_HOTELS_VARIANTS,
  MOBILE_RECENTLY_VIEWED_HOTELS,
  MOBILE_RECENTLY_VIEWED_HOTELS_VARIANTS,
  RECENTLY_VIEWED_MASTER_V1,
  SIMILAR_HOTELS_EXPERIMENT,
  SIMILAR_HOTELS_VARIANTS,
  TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  XMOD_V1_EXPERIMENT,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  useExperiments,
} from "../../context/experiments";
import { PORTAL_TITLE, SELECT_ROOM_TITLE } from "../../lang/textConstants";
import { RefundableRoomsCfarDetails } from "../ancillary/components/addOnComponents/cfar/RefundableRoomsCfarDetails";
import { ConnectedWishlistManagementModal } from "../wishlist/components/WishlistManagementModal/container";
import { WishlistSnackbar } from "../wishlist/components/WishlistSnackbar/component";
import { DesktopShop } from "./components/DesktopShop";
import { MobileShop } from "./components/MobileShop";
import { HotelShopConnectorProps } from "./container";
import { HotelShopCallState } from "./reducer/state";
import "./styles.scss";
import { createCustomRefundableRoomDetailsV2 } from "./utils/customRefundableRoomDetails";
import { handlePageRedirectOnSelectRoomProduct } from "./utils/queryStringHelpers";
import { shouldTrackLodgingLocation } from "./utils/shouldTrackLodgingLocation";

const getQuoteId = (roomProductVariant: RoomProductVariant | undefined) =>
  roomProductVariant?.variant === RoomProductType.RefundableRoom
    ? roomProductVariant.quoteId
    : undefined;

export interface IHotelShopProps
  extends HotelShopConnectorProps,
    RouteComponentProps {}

export const HotelShop = (props: IHotelShopProps) => {
  const {
    selectedLodging,
    fetchHotelShop,
    setSelectedAccount,
    history,
    rewardsAccounts,
    viewedHotelDetailsProperties,
    fetchTravelWalletDetails,
    isAddOnOptionAvailable,
    roomInfoProducts,
    roomInfoProductsWithTransformedIndexes,
    hasSelectedRefundableRoom,
    setHasSelectedRefundableRoom,
    setSelectedCfarId,
    selectRoomType,
    refundableRoomProductWithSelectedCfarId,
    refundableRoomProductCorrespondingToChosenRoom,
    getHotelCfarOfferChoiceProperties,
    fetchTravelWalletCreditHistory,
    hotelShopCallState,
    hotelShopDateRange,
    setSelectedRoomId,
    setHasAddedCfarFromModal,
    listPaymentMethods,

    setSelectedLodgingIsFavorited,
  } = props;
  const { matchesDesktop, matchesMobile } = useDeviceTypes();
  const clientContext = useContext(ClientContext);
  const { isAgentPortal } = clientContext;
  const [isReadyToRedirect, setIsReadyToRedirect] = useState<boolean>(false);
  const [openRefundableRoomDetails, setOpenRefundableRoomDetails] =
    useState<boolean>(false);
  const [refundableRoomProduct, setRefundableRoomProduct] =
    useState<RoomProductWithTransformedIndexes>();
  const [roomInfoProduct, setRoomInfoProduct] =
    useState<RoomInfoProductsWithTransformedIndexes>();
  const [roomProductIndex, setRoomProductIndex] = useState<number>();
  const {
    trackingEventControl,
    updateTrackingEventControl,
    removeEventFromTrackingEventControl,
  } = useTrackingEventControl();
  const expState = useExperiments();

  const wishlistExperiment = getExperimentVariant(
    expState.experiments,
    STAYS_WISHLIST
  );
  const isWishlistExperiment = useMemo(
    () => wishlistExperiment === AVAILABLE,
    [wishlistExperiment]
  );

  const similarHotelsVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    SIMILAR_HOTELS_EXPERIMENT,
    SIMILAR_HOTELS_VARIANTS
  );

  const travelCreditHistoryExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_CREDIT_HISTORY_EXPERIMENT
  );
  const isTravelCreditHistoryExperiment = useMemo(
    () => travelCreditHistoryExperiment === AVAILABLE,
    [travelCreditHistoryExperiment]
  );

  const recentlyViewedExperiment = getExperimentVariant(
    expState.experiments,
    RECENTLY_VIEWED_MASTER_V1
  );
  const isRecentlyViewedV1Experiment = useMemo(
    () => recentlyViewedExperiment === AVAILABLE,
    [recentlyViewedExperiment]
  );

  const recentlyViewedDesktopP0Variant = getExperimentVariantCustomVariants(
    expState.experiments,
    DESKTOP_RECENTLY_VIEWED_HOTELS,
    DESKTOP_RECENTLY_VIEWED_HOTELS_VARIANTS
  );

  const recentlyViewedMobileP0Variant = getExperimentVariantCustomVariants(
    expState.experiments,
    MOBILE_RECENTLY_VIEWED_HOTELS,
    MOBILE_RECENTLY_VIEWED_HOTELS_VARIANTS
  );
  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;
  const queryString = useMemo(
    () => queryStringParser.parse(history.location.search),
    [history.location.search]
  );

  useGetAllWishlistItemsByUserQuery(undefined, {
    skip: !isWishlistExperiment,
  });
  useGetAllWishlistsQuery(undefined, {
    skip: !isWishlistExperiment,
  });

  const {
    data: favoritedLodgings,
    isSuccess: isFavoritedLodgingsSuccess,
    isError: isFavoritedLodgingsError,
  } = useSelector(wishlistsApi.endpoints.getAllWishlistItemsByUser.select());

  const isWishlistsLoaded =
    isFavoritedLodgingsSuccess || isFavoritedLodgingsError;

  const isFavorited = favoritedLodgings?.has(selectedLodging?.lodging.id || "");

  const handleReadyToRedirect = useCallback(
    (eventControl?: TrackingEventControlType) => {
      // note: it needs to wait for `selectRoomType` to be executed before proceeding with the page redirect
      setTimeout(() => {
        if (eventControl) {
          updateTrackingEventControl(eventControl);
        }
        setIsReadyToRedirect(true);
      });
    },
    [updateTrackingEventControl]
  );

  const roomInfoProductsType: HotelShopRoomTypePickerVariant = useMemo(
    () => ({
      roomInfoProductsWithTransformedIndexes,
      customComponents: {
        RefundableRoomDetails: createCustomRefundableRoomDetailsV2({
          setRefundableRoomProduct,
          checkInDate: hotelShopDateRange?.from,
          checkInTime:
            selectedLodging?.lodging.checkInInstructions?.checkInTime,
          setOpenRefundableRoomDetails,
          isMobile: matchesMobile,
          isXmodRedesign,
        }),
      },
      customStates: {
        // note: refundable room is disabled on agent portal
        isRefundableRoomDisabled: isAgentPortal,
        hasSelectedRefundableRoom,
        setHasSelectedRefundableRoom: (
          hasSelectedRefundableRoom: boolean,
          quoteId?: QuoteId
        ) => {
          setHasSelectedRefundableRoom(hasSelectedRefundableRoom);
          setSelectedCfarId(
            hasSelectedRefundableRoom && !!quoteId ? quoteId : null
          );
        },
      },
      variant: HotelShopRoomTypePickerEnum.WithTransformedIndexes,
    }),
    [
      roomInfoProductsWithTransformedIndexes,
      setRefundableRoomProduct,
      setOpenRefundableRoomDetails,
      matchesMobile,
      hotelShopDateRange?.from,
      selectedLodging?.price?.displayTaxesAndFeesIncluded,
      isAgentPortal,
      hasSelectedRefundableRoom,
      setHasSelectedRefundableRoom,
      setSelectedCfarId,
    ]
  );

  useEffect(() => {
    if (selectedLodging) {
      document.title = selectedLodging.lodging.name;
    }
  }, [selectedLodging]);

  useEffect(() => {
    fetchTravelWalletDetails();
    listPaymentMethods();
    document.title = SELECT_ROOM_TITLE;
    setTimeout(() => window.scrollTo(0, 0), 0);

    return () => {
      document.title = PORTAL_TITLE;
    };
  }, []);

  useEffect(() => {
    if (isWishlistExperiment && !isWishlistsLoaded) {
      return;
    }
    if (hotelShopCallState === HotelShopCallState.Success) {
      const { properties, encryptedProperties } = viewedHotelDetailsProperties;
      const offer = selectedLodging?.bestOfferThisLodging;
      const offerProperties = {
        viewed_travel_offer: !!offer,
      };
      trackEvent({
        eventName: VIEWED_HOTEL_DETAILS,
        properties: {
          ...properties,
          ...getViewedHotelDetailsHotelCfarOfferFacts(
            refundableRoomProductWithSelectedCfarId
          ),
          ...offerProperties,
          nth_night_offer: getNthNightTrackingProperty(selectedLodging),
          ...(isWishlistExperiment && {
            is_favorited: isFavorited,
          }),
        },
        encryptedProperties,
      });
    }
  }, [hotelShopCallState, isWishlistExperiment, isWishlistsLoaded]);

  // Keep track of the favorited status of the selected hotel for tracking in the booking flow
  useEffect(() => {
    if (
      isWishlistExperiment &&
      isWishlistsLoaded &&
      isFavorited !== undefined
    ) {
      setSelectedLodgingIsFavorited(isFavorited);
    }
  }, [
    isWishlistExperiment,
    isWishlistsLoaded,
    isFavorited,
    setSelectedLodgingIsFavorited,
  ]);

  useEffect(() => {
    if (
      [CallState.Success, CallState.Failed].includes(expState.callState) ||
      expState.experiments.length > 0
    ) {
      fetchHotelShop(history, {
        overrideStateByQueryParams: true,
        fetchSimilarHotels: similarHotelsVariant !== CONTROL,
        includeLocationSearchTerm:
          (matchesMobile
            ? recentlyViewedMobileP0Variant !== CONTROL
            : recentlyViewedDesktopP0Variant !== CONTROL) &&
          isRecentlyViewedV1Experiment &&
          shouldTrackLodgingLocation(history.location.search),
      });
      setTimeout(() => window.scrollTo(0, 0), 0);
    }
  }, [expState.callState, queryString.lodgingId]);

  useEffect(() => {
    const { selectedAccountIndex } = queryStringParser.parse(
      history.location.search
    );

    if (selectedAccountIndex && rewardsAccounts.length > 0) {
      setSelectedAccount(
        rewardsAccounts[Number(selectedAccountIndex)]?.accountReferenceId
      );
    }
  }, [rewardsAccounts]);

  const { isTravelApp } = useIsTravelApp();

  /*
    note: selectRoomType (the action that updates indexes in redux state) is called within a halifax component as a callback;
    it's executed at the same time as when `onClickContinue` is handled, which means that some states are not yet updated.
  */

  useEffect(() => {
    if (isReadyToRedirect) {
      // delayed firing of SELECT_HOTEL_ROOM
      if (trackingEventControl[SELECT_HOTEL_ROOM]) {
        const { properties, encryptedProperties } =
          viewedHotelDetailsProperties;
        trackEvent({
          eventName: SELECT_HOTEL_ROOM,
          properties: {
            ...properties,
            ...trackingEventControl[SELECT_HOTEL_ROOM].properties,
            ...getSelectHotelRoomHotelCfarOfferFacts({
              refundableRoomProduct:
                refundableRoomProductCorrespondingToChosenRoom,
              ...getHotelCfarOfferChoiceProperties("shop"),
            }),
          },
          encryptedProperties,
        });
        removeEventFromTrackingEventControl(SELECT_HOTEL_ROOM);
      }

      handlePageRedirectOnSelectRoomProduct({
        history,
        isAddOnOptionAvailable,
        hotelBookType: HotelBookType.DEFAULT,
        trackBookStep: isTravelApp,
      });
      setIsReadyToRedirect(false);
    }
  }, [
    history,
    isAddOnOptionAvailable,
    isReadyToRedirect,
    trackingEventControl,
  ]);

  const handleRefundableRoomModalContinue = useCallback(() => {
    // note: refundable room is disabled on agent portal
    if (isAgentPortal) {
      return;
    }

    setHasAddedCfarFromModal(true);
    const quoteId = getQuoteId(refundableRoomProduct?.roomProductVariant);
    const baseProduct = refundableRoomProduct?.roomProductVariant?.baseProduct;

    if (quoteId != null) {
      setHasSelectedRefundableRoom(true);
      setSelectedCfarId(quoteId);
    }
    if (roomInfoProduct) {
      setSelectedRoomId(roomInfoProduct.roomInfo.roomId);
    }

    if (baseProduct != null) {
      selectRoomType(
        baseProduct.roomIndex,
        baseProduct.productIndex,
        baseProduct.rateId?.value
      );
      handleReadyToRedirect({
        [SELECT_HOTEL_ROOM]: {
          properties: {
            room_type: roomInfoProducts[baseProduct.roomIndex]?.roomInfo.name,
            rate_provider:
              roomInfoProducts[baseProduct.roomIndex]?.products[
                baseProduct.productIndex
              ]?.providerName,
            provider_name:
              roomInfoProducts[baseProduct.roomIndex]?.products[
                baseProduct.productIndex
              ]?.providerName,
          },
        },
      });
    }
  }, [
    isAgentPortal,
    setHasAddedCfarFromModal,
    refundableRoomProduct,
    setHasSelectedRefundableRoom,
    setSelectedCfarId,
    selectRoomType,
    handleReadyToRedirect,
    roomInfoProducts,
  ]);

  useEffect(() => {
    if (isTravelCreditHistoryExperiment) {
      fetchTravelWalletCreditHistory();
    }
  }, [isTravelCreditHistoryExperiment]);

  return (
    <>
      <Box className="hotel-shop-root">
        {matchesDesktop && (
          <DesktopShop
            handleReadyToRedirect={handleReadyToRedirect}
            roomInfoProductsType={roomInfoProductsType}
            setRoomInfoProduct={setRoomInfoProduct}
            setRoomProductIndex={setRoomProductIndex}
          />
        )}
        {matchesMobile && (
          <MobileShop
            handleReadyToRedirect={handleReadyToRedirect}
            roomInfoProductsType={roomInfoProductsType}
          />
        )}
      </Box>
      {refundableRoomProduct?.roomProductVariant?.variant ===
        RoomProductType.RefundableRoom && (
        <Box>
          <RefundableRoomsCfarDetails
            openCfarDetails={openRefundableRoomDetails}
            onClose={() => setOpenRefundableRoomDetails(false)}
            onContinue={handleRefundableRoomModalContinue}
            currentHotelCfarQuote={{
              id: refundableRoomProduct.roomProductVariant.quoteId,
              premiumPrices:
                refundableRoomProduct.roomProductVariant.premiumPrices,
              premiumPricesPerNight:
                refundableRoomProduct.roomProductVariant.premiumPricesPerNight,
              coverageAmount:
                refundableRoomProduct.roomProductVariant.coverageAmount,
              coveragePercentage:
                refundableRoomProduct.roomProductVariant.coveragePercentage,
              CfarQuoteResult: CfarQuoteResultEnum.HotelCfarQuote,
              expirationDate:
                refundableRoomProduct.roomProductVariant.expirationDate,
            }}
            currentAdditionalInfo={
              refundableRoomProduct.roomProductVariant.additionalInfo
            }
            contentClassName="hotel-refundable-room-details-modal"
            roomInfoProduct={roomInfoProduct}
            roomProductIndex={roomProductIndex}
          />
        </Box>
      )}
      <ConnectedWishlistManagementModal
        history={history}
        entryType="hotel_shop"
      />
      <WishlistSnackbar />
    </>
  );
};
